import noiseSrc from "../gl/glsl/psrdnoise2.glsl";
import simFragSrc from "../gl/glsl/sim.frag";
import simVertSrc from "../gl/glsl/sim.vert";
import {
  copyBuffer,
  createParticleVao,
  createStateBuffer,
  must,
} from "../gl/buffers";
import { compileProgram, type LinkedProgram } from "../gl/shader";
import { MAX_OCTAVES } from "../prng";
import { BLEED, FLOATS_PER_PARTICLE } from "./state";

/** Fixed timestep — canonical time is stepCount * DT, never wall clock. */
export const DT = 1 / 60;

/**
 * Steps until a plot is settled: the last-born strand (stagger) with the
 * longest possible lifetime roll (base * (1 + jitter/2)) has died, plus a
 * two-step guard for fp accumulation at the boundaries.
 */
export function plotTotalSteps(
  lifetime: number,
  lifetimeJitter: number,
  stagger: number,
): number {
  return Math.ceil((stagger + lifetime * (1 + lifetimeJitter * 0.5)) / DT) + 2;
}

export interface SimUniforms {
  speed: number;
  wind: [number, number];
  vortex: number;
  attract: number;
  aspect: number;
  flowScale: number;
  octaves: number;
  roughness: number;
  flowStrength: number;
  evolution: number;
  octaveOffsets: Float32Array; // MAX_OCTAVES x vec2, flattened
}

export function createSimProgram(gl: WebGL2RenderingContext): LinkedProgram {
  return compileProgram(gl, {
    vert: simVertSrc,
    frag: simFragSrc,
    vertChunks: [noiseSrc],
    defines: { MAX_OCTAVES },
    tfVaryings: ["v_posAgeLife", "v_rand"],
    attribLocations: { a_posAgeLife: 0, a_rand: 1 },
  });
}

/**
 * Transform-feedback ping-pong over two interleaved state VBOs. The export
 * path uses captureTo/restore to backup the live state around its replay.
 */
export class Simulation {
  stepCount = 0;

  private buffers!: [WebGLBuffer, WebGLBuffer];
  private vaos!: [WebGLVertexArrayObject, WebGLVertexArrayObject];
  private current = 0;
  private tf: WebGLTransformFeedback;
  private particleCount = 0;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly prog: LinkedProgram,
    initialState: Float32Array,
  ) {
    this.tf = must(gl.createTransformFeedback(), "transform feedback");
    this.allocate(initialState);
  }

  get count(): number {
    return this.particleCount;
  }

  get byteLength(): number {
    return this.particleCount * FLOATS_PER_PARTICLE * 4;
  }

  /** VAO reading the most recently written state — feed to the dot pass. */
  get currentVao(): WebGLVertexArrayObject {
    return this.vaos[this.current]!;
  }

  private allocate(state: Float32Array): void {
    const gl = this.gl;
    this.particleCount = state.length / FLOATS_PER_PARTICLE;
    const bytes = state.byteLength;
    const a = createStateBuffer(gl, state);
    const b = createStateBuffer(gl, bytes);
    this.buffers = [a, b];
    this.vaos = [createParticleVao(gl, a), createParticleVao(gl, b)];
    this.current = 0;
  }

  private freeBuffers(): void {
    const gl = this.gl;
    for (const vao of this.vaos) gl.deleteVertexArray(vao);
    for (const buf of this.buffers) gl.deleteBuffer(buf);
  }

  /**
   * Replace the strand state (re-plot / reseed / reinit-class change).
   * stepCount continues from atStep (0 for a fresh plot).
   */
  reset(state: Float32Array, atStep: number): void {
    const gl = this.gl;
    this.stepCount = atStep;
    if (state.length / FLOATS_PER_PARTICLE !== this.particleCount) {
      this.freeBuffers();
      this.allocate(state);
      return;
    }
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers[this.current]!);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, state);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }

  /** One fixed-dt step: TF ping-pong with rasterizer discard. */
  step(u: SimUniforms): void {
    const gl = this.gl;
    const loc = this.prog.uniform;
    const src = this.current;
    const dst = 1 - src;

    gl.useProgram(this.prog.program);
    gl.uniform1f(loc("u_dt"), DT);
    gl.uniform1f(loc("u_time"), this.stepCount * DT);
    gl.uniform1f(loc("u_speed"), u.speed);
    gl.uniform2f(loc("u_wind"), u.wind[0], u.wind[1]);
    gl.uniform1f(loc("u_vortex"), u.vortex);
    gl.uniform1f(loc("u_attract"), u.attract);
    gl.uniform2f(loc("u_domain"), u.aspect + 2 * BLEED, 1 + 2 * BLEED);
    gl.uniform1f(loc("u_flowScale"), u.flowScale);
    gl.uniform1i(loc("u_octaves"), u.octaves);
    gl.uniform1f(loc("u_roughness"), u.roughness);
    gl.uniform1f(loc("u_flowStrength"), u.flowStrength);
    gl.uniform1f(loc("u_evolution"), u.evolution);
    gl.uniform2fv(loc("u_octaveOffset"), u.octaveOffsets);

    gl.bindVertexArray(this.vaos[src]!);
    gl.enable(gl.RASTERIZER_DISCARD);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.tf);
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, this.buffers[dst]!);
    gl.beginTransformFeedback(gl.POINTS);
    gl.drawArrays(gl.POINTS, 0, this.particleCount);
    gl.endTransformFeedback();
    // Unbind from the TF target before the buffer is used as an attribute
    // source again (spec-required, silently broken on some drivers if not).
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, null);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
    gl.disable(gl.RASTERIZER_DISCARD);
    gl.bindVertexArray(null);

    this.current = dst;
    this.stepCount += 1;
  }

  /** Copy the live state out (export backup). */
  captureTo(buffer: WebGLBuffer): void {
    copyBuffer(this.gl, this.buffers[this.current]!, buffer, this.byteLength);
  }

  /** Copy a saved state in and rewind/restore the step counter. */
  restore(buffer: WebGLBuffer, step: number): void {
    copyBuffer(this.gl, buffer, this.buffers[this.current]!, this.byteLength);
    this.stepCount = step;
  }

  destroy(): void {
    this.freeBuffers();
    this.gl.deleteTransformFeedback(this.tf);
  }
}
